import Image from '@/features/shared/Image';
import {
  MuiCard,
  MuiCardContent,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import clsx from 'clsx';
import React, { createRef, memo, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import useStyles from './ContentCardSmall.styles';

const ContentCardSmallContent = memo(({ data, cardColor, theme }: any) => {
  const headerRef = createRef<HTMLDivElement>();
  const classes = useStyles({});
  const { cardImage, cardCategory, cardDescription, cardHeading } = data || {};
  const [category] = !Array.isArray(cardCategory) ? [cardCategory] : cardCategory;

  useLayoutEffect(() => {
    if (headerRef?.current?.clientHeight) {
      const headerHeight = headerRef?.current?.clientHeight;
      const contentEl = headerRef?.current?.closest('.card-content') as HTMLDivElement;
      contentEl.style.top = `calc(100% - ${headerHeight + 36}px)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentHeadingClassName = clsx(classes.contentHeading, 'gtm-tab-title');
  const copyClassName = clsx(classes.copy, 'card-description', 'current');
  const contentClassName = clsx(classes.content, `bg-color-${cardColor}`, 'card-content');
  const innerContentClassName = clsx(classes.innerContent, `card-color-${cardColor}`);

  return (
    <>
      {cardImage && (
        <MuiCardMedia className={clsx('card-image', classes.image)}>
          <Image
            image={cardImage}
            type="card"
            size="cover"
            position="center"
            roundedCorners={false}
          />
        </MuiCardMedia>
      )}
      <MuiCardContent className={contentClassName}>
        <div className={innerContentClassName}>
          <div className={classes.heading} ref={headerRef}>
            {category && (
              <MuiTypography variant="overline" component="h5" className={classes.contentCategory}>
                {category.label || category}
              </MuiTypography>
            )}
            {cardHeading && (
              <MuiTypography variant="body1" component="h4" className={contentHeadingClassName}>
                {cardHeading}
              </MuiTypography>
            )}
          </div>
          {cardDescription && (
            <RichText
              theme={theme}
              className={copyClassName}
              content={cardDescription}
              customStyles
            />
          )}
        </div>
      </MuiCardContent>
    </>
  );
});

const ContentCardSmall = ({
  data = [],
  cardColor,
  theme,
  className,
  elevation = 8,
  initialIndex = 0,
  index = 0,
}: {
  data?: CardType[];
  cardColor?: string;
  theme?: ThemeType;
  className?: string;
  elevation?: number;
  initialIndex?: number;
  index?: number;
}): JSX.Element => {
  const [slider, setSlider] = useState<any>(null);
  const classes = useStyles({});

  const sliderProps = useMemo(
    () => ({
      spaceBetween: 0,
      slidesPerView: 1,
      loop: true,
      loopedSlides: data.length,
      roundLengths: true,
      allowTouchMove: false,
      onSwiper: (swiper: any) => {
        setSlider(swiper);
        swiper.slideTo(initialIndex, 0, false);
      },
      /* For DataLayer purposes this component shouldn't be considered since it's always wrapped. */
    }),
    [data.length, initialIndex],
  );

  useEffect(() => {
    if (slider && !slider.destroyed && data.length > 1) {
      slider.slideTo(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const rootClassName = clsx(
    classes.root,
    className,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    'card-root',
  );

  const slides = useMemo(
    () =>
      data.map((cData, idx) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <SwiperSlide key={`card_${idx}`}>
            <ContentCardSmallContent data={cData} cardColor={cardColor} theme={theme} />
          </SwiperSlide>
        );
      }),
    [cardColor, data, theme],
  );

  return (
    <MuiCard className={clsx(rootClassName, 'gntc-content-card-small')} elevation={elevation}>
      {data.length > 1 ? (
        <SwiperCarousel
          slider={slider}
          className={classes.carousel}
          {...sliderProps}
          classes={classes}
        >
          {slides}
        </SwiperCarousel>
      ) : (
        data[0] && <ContentCardSmallContent data={data[0]} cardColor={cardColor} theme={theme} />
      )}
    </MuiCard>
  );
};

export default memo(ContentCardSmall);
